/**
 * Shared threshold for "the app is stuck loading". Used both by the global
 * pending UI (GlobalPending) and the auth route guards so there is a single
 * source of truth for when we give up and surface a reload affordance.
 */
export const LOAD_STALL_MS = 10_000;

/** Extended stall threshold for slow connections — see getLoadStallMs(). */
export const SLOW_LOAD_STALL_MS = 20_000;

const SLOW_EFFECTIVE_TYPES = new Set(["slow-2g", "2g", "3g"]);

/**
 * Stall threshold tuned to the connection. Good links keep LOAD_STALL_MS; on
 * 2g/3g/slow-2g or Data Saver we wait SLOW_LOAD_STALL_MS before surfacing the
 * reload screen, so genuinely-slow users who would still succeed aren't shown an
 * error prematurely. navigator.connection is Chrome/Android only — read
 * defensively (absent → fall back to the base threshold).
 */
export function getLoadStallMs(): number {
  const conn = (
    navigator as Navigator & {
      connection?: { effectiveType?: string; saveData?: boolean };
    }
  ).connection;
  if (
    conn &&
    (SLOW_EFFECTIVE_TYPES.has(conn.effectiveType ?? "") || conn.saveData)
  ) {
    return SLOW_LOAD_STALL_MS;
  }
  return LOAD_STALL_MS;
}

/** Sentinel returned by withTimeout when the promise didn't settle in time. */
export const TIMEOUT = Symbol("timeout");

/**
 * Race a promise against a timeout. Resolves to the promise's value, or the
 * TIMEOUT sentinel if `ms` elapses first. Never rejects on timeout — callers
 * branch on the sentinel. The timer is always cleared so it can't leak.
 *
 * Note: this only bounds how long the *caller* waits — it does not abort the
 * underlying work (e.g. supabase's getSession keeps running, holding its
 * navigator.locks auth lock). A full page reload is what actually recovers.
 */
export function withTimeout<T>(
  promise: Promise<T>,
  ms: number,
): Promise<T | typeof TIMEOUT> {
  let timer: ReturnType<typeof setTimeout>;
  const timeout = new Promise<typeof TIMEOUT>((resolve) => {
    timer = setTimeout(() => resolve(TIMEOUT), ms);
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
}
